import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { QualificationState } from "@reducers/types/HRM/qualification-state";

import {
  getQualificationRequest,
  updateEducationsInfoRequest,
  updateLocalTrainingsInfoRequest,
  updateForeignTrainingsInfoRequest,
  updateAdditionalProfessionalQualificationsInfoRequest,
  updatePublicationsInfoRequest,
  updateHonoursInfoRequest,
  updateRewardsInfoRequest,
  deleteEducationInfoRequest,
  deleteLocalTrainingInfoRequest,
  deleteForeignTrainingInfoRequest,
  deleteAdditionalProfessionalQualificationInfoRequest,
  deletePublicationInfoRequest,
  deleteHonourInfoRequest,
  deleteRewardInfoRequest,
} from "@actions/HRM/employee/qualification-actions";
import { getQualificationState } from "@selectors/HRM/employee/qualification-info-selector";

import { useAppSelector } from "@helpers/redux";
import {
  QualificationType,
  EducationType,
  LocalTrainingType,
  ForeignTrainingType,
  AdditionalProfessionalQualificationType,
  PublicationType,
  HonourType,
  RewardType,
} from "@type/HRM/qualification";

interface UseQualifications {
  getQualificationInfoRequestOngoing: boolean;

  updateEducationRequestOngoing: boolean;
  updateEducationSuccess: boolean;
  updateEducationFailed: boolean;

  updateLocalTrainingRequestOngoing: boolean;
  updateLocalTrainingSuccess: boolean;
  updateLocalTrainingFailed: boolean;

  updateForeignTrainingRequestOngoing: boolean;
  updateForeignTrainingSuccess: boolean;
  updateForeignTrainingFailed: boolean;

  updateAdditionalProfessionalQualificationRequestOngoing: boolean;
  updateAdditionalProfessionalQualificationSuccess: boolean;
  updateAdditionalProfessionalQualificationFailed: boolean;

  updatePublicationRequestOngoing: boolean;
  updatePublicationSuccess: boolean;
  updatePublicationFailed: boolean;

  updateHonourRequestOngoing: boolean;
  updateHonourSuccess: boolean;
  updateHonourFailed: boolean;

  updateRewardRequestOngoing: boolean;
  updateRewardSuccess: boolean;
  updateRewardFailed: boolean;

  deleteEducationRequestOngoing: boolean;
  deleteEducationSuccess: boolean;
  deleteEducationFailed: boolean;

  deleteLocalTrainingRequestOngoing: boolean;
  deleteLocalTrainingSuccess: boolean;
  deleteLocalTrainingFailed: boolean;

  deleteForeignTrainingRequestOngoing: boolean;
  deleteForeignTrainingSuccess: boolean;
  deleteForeignTrainingFailed: boolean;

  deleteAdditionalProfessionalQualificationRequestOngoing: boolean;
  deleteAdditionalProfessionalQualificationSuccess: boolean;
  deleteAdditionalProfessionalQualificationFailed: boolean;

  deletePublicationRequestOngoing: boolean;
  deletePublicationSuccess: boolean;
  deletePublicationFailed: boolean;

  deleteHonourRequestOngoing: boolean;
  deleteHonourSuccess: boolean;
  deleteHonourFailed: boolean;

  deleteRewardRequestOngoing: boolean;
  deleteRewardSuccess: boolean;
  deleteRewardFailed: boolean;

  qualificationInfo: QualificationType;

  changeEducationsInfo: (updateEducationInfo: EducationType) => void;
  changeLocalTrainingsInfo: (
    updateLocalTrainingInfo: LocalTrainingType,
  ) => void;
  changeForeignTrainingsInfo: (
    updateForeignTrainingInfo: ForeignTrainingType,
  ) => void;
  changeAdditionalProfessionalQualificationsInfo: (
    updateAdditionalProfessionalQualificationsInfo: AdditionalProfessionalQualificationType,
  ) => void;
  changePublicationsInfo: (updatePublicationInfo: PublicationType) => void;
  changeHonoursInfo: (updateHonourInfo: HonourType) => void;
  changeRewardsInfo: (updateRewardInfo: RewardType) => void;

  removeEducationInfo: (educationID: number) => void;
  removeLocalTrainingInfo: (locationID: number) => void;
  removeForeignTrainingInfo: (foreignTrainingID: number) => void;
  removeAdditionalProfessionalQualificationInfo: (
    additionalProfessionalQualificationID: number,
  ) => void;
  removePublicationInfo: (publicationID: number) => void;
  removeHonourInfo: (honourID: number) => void;
  removeRewardInfo: (rewardID: number) => void;
}

export const useQualifications = (id: number | null): UseQualifications => {
  const dispatch = useDispatch();
  const {
    data,

    getQualificationInfoRequestOngoing,

    updateEducationRequestOngoing,
    updateEducationSuccess,
    updateEducationFailed,

    updateLocalTrainingRequestOngoing,
    updateLocalTrainingSuccess,
    updateLocalTrainingFailed,

    updateForeignTrainingRequestOngoing,
    updateForeignTrainingSuccess,
    updateForeignTrainingFailed,

    updateAdditionalProfessionalQualificationRequestOngoing,
    updateAdditionalProfessionalQualificationSuccess,
    updateAdditionalProfessionalQualificationFailed,

    updatePublicationRequestOngoing,
    updatePublicationSuccess,
    updatePublicationFailed,

    updateHonourRequestOngoing,
    updateHonourSuccess,
    updateHonourFailed,

    updateRewardRequestOngoing,
    updateRewardSuccess,
    updateRewardFailed,

    deleteEducationRequestOngoing,
    deleteEducationSuccess,
    deleteEducationFailed,

    deleteLocalTrainingRequestOngoing,
    deleteLocalTrainingSuccess,
    deleteLocalTrainingFailed,

    deleteForeignTrainingRequestOngoing,
    deleteForeignTrainingSuccess,
    deleteForeignTrainingFailed,

    deleteAdditionalProfessionalQualificationRequestOngoing,
    deleteAdditionalProfessionalQualificationSuccess,
    deleteAdditionalProfessionalQualificationFailed,

    deletePublicationRequestOngoing,
    deletePublicationSuccess,
    deletePublicationFailed,

    deleteHonourRequestOngoing,
    deleteHonourSuccess,
    deleteHonourFailed,

    deleteRewardRequestOngoing,
    deleteRewardSuccess,
    deleteRewardFailed,
  } = useAppSelector<QualificationState>(getQualificationState);

  //GET
  useEffect(() => {
    dispatch(getQualificationRequest(id));
  }, [dispatch, id]);

  //UPDATE EDUCATION INFO
  const changeEducationsInfo = (updateEducationInfo: EducationType) =>
    dispatch(
      updateEducationsInfoRequest({
        employeeID: id,
        ...updateEducationInfo,
      } as EducationType),
    );

  //UPDATE LOCAL TRAING INFO
  const changeLocalTrainingsInfo = (
    updateLocalTrainingInfo: LocalTrainingType,
  ) =>
    dispatch(
      updateLocalTrainingsInfoRequest({
        employeeID: id,
        ...updateLocalTrainingInfo,
      } as LocalTrainingType),
    );

  //UPDATE FOREIGN TRAING INFO
  const changeForeignTrainingsInfo = (
    updateForeignTrainingInfo: ForeignTrainingType,
  ) =>
    dispatch(
      updateForeignTrainingsInfoRequest({
        employeeID: id,
        ...updateForeignTrainingInfo,
      } as ForeignTrainingType),
    );

  //UPDATE ADDITIONAL PROFESSIONAL QUALIFICATION INFO
  const changeAdditionalProfessionalQualificationsInfo = (
    updateAdditionalProfessionalQualificationInfo: AdditionalProfessionalQualificationType,
  ) =>
    dispatch(
      updateAdditionalProfessionalQualificationsInfoRequest({
        employeeID: id,
        ...updateAdditionalProfessionalQualificationInfo,
      } as AdditionalProfessionalQualificationType),
    );

  //UPDATE PUBLICATION INFO
  const changePublicationsInfo = (updatePublicationInfo: PublicationType) =>
    dispatch(
      updatePublicationsInfoRequest({
        employeeID: id,
        ...updatePublicationInfo,
      } as PublicationType),
    );

  //UPDATE HONOUR INFO
  const changeHonoursInfo = (updateHonourInfo: HonourType) =>
    dispatch(
      updateHonoursInfoRequest({
        employeeID: id,
        ...updateHonourInfo,
      } as HonourType),
    );

  //UPDATE REWARD INFO
  const changeRewardsInfo = (updateRewardInfo: RewardType) =>
    dispatch(
      updateRewardsInfoRequest({
        employeeID: id,
        ...updateRewardInfo,
      } as RewardType),
    );

  //DELETE EDUCATION INFO
  const removeEducationInfo = (educationID: number) =>
    dispatch(
      deleteEducationInfoRequest({
        employeeID: id,
        educationID,
      }),
    );

  //DELETE LOCAL TRAINING INFO
  const removeLocalTrainingInfo = (localTrainingID: number) =>
    dispatch(
      deleteLocalTrainingInfoRequest({
        employeeID: id,
        localTrainingID,
      }),
    );

  //DELETE FOREIGN TRAINING INFO
  const removeForeignTrainingInfo = (foreignTrainingID: number) =>
    dispatch(
      deleteForeignTrainingInfoRequest({
        employeeID: id,
        foreignTrainingID,
      }),
    );

  //DELETE ADDITIONAL PROFESSIONAL QUALIFICATION INFO
  const removeAdditionalProfessionalQualificationInfo = (
    additionalProfessionalQualificationID: number,
  ) =>
    dispatch(
      deleteAdditionalProfessionalQualificationInfoRequest({
        employeeID: id,
        additionalProfessionalQualificationID,
      }),
    );

  //DELETE PUBLICATION INFO
  const removePublicationInfo = (publicationID: number) =>
    dispatch(
      deletePublicationInfoRequest({
        employeeID: id,
        publicationID,
      }),
    );

  //DELETE HONOUR INFO
  const removeHonourInfo = (honourID: number) =>
    dispatch(
      deleteHonourInfoRequest({
        employeeID: id,
        honourID,
      }),
    );

  //DELETE REWARD INFO
  const removeRewardInfo = (rewardID: number) =>
    dispatch(
      deleteRewardInfoRequest({
        employeeID: id,
        rewardID,
      }),
    );

  return {
    getQualificationInfoRequestOngoing,

    updateEducationRequestOngoing,
    updateEducationSuccess,
    updateEducationFailed,

    updateLocalTrainingRequestOngoing,
    updateLocalTrainingSuccess,
    updateLocalTrainingFailed,

    updateForeignTrainingRequestOngoing,
    updateForeignTrainingSuccess,
    updateForeignTrainingFailed,

    updateAdditionalProfessionalQualificationRequestOngoing,
    updateAdditionalProfessionalQualificationSuccess,
    updateAdditionalProfessionalQualificationFailed,

    updatePublicationRequestOngoing,
    updatePublicationSuccess,
    updatePublicationFailed,

    updateHonourRequestOngoing,
    updateHonourSuccess,
    updateHonourFailed,

    updateRewardRequestOngoing,
    updateRewardSuccess,
    updateRewardFailed,

    deleteEducationRequestOngoing,
    deleteEducationSuccess,
    deleteEducationFailed,

    deleteLocalTrainingRequestOngoing,
    deleteLocalTrainingSuccess,
    deleteLocalTrainingFailed,

    deleteForeignTrainingRequestOngoing,
    deleteForeignTrainingSuccess,
    deleteForeignTrainingFailed,

    deleteAdditionalProfessionalQualificationRequestOngoing,
    deleteAdditionalProfessionalQualificationSuccess,
    deleteAdditionalProfessionalQualificationFailed,

    deletePublicationRequestOngoing,
    deletePublicationSuccess,
    deletePublicationFailed,

    deleteHonourRequestOngoing,
    deleteHonourSuccess,
    deleteHonourFailed,

    deleteRewardRequestOngoing,
    deleteRewardSuccess,
    deleteRewardFailed,

    qualificationInfo: data as QualificationType,

    changeEducationsInfo,
    changeLocalTrainingsInfo,
    changeForeignTrainingsInfo,
    changeAdditionalProfessionalQualificationsInfo,
    changePublicationsInfo,
    changeHonoursInfo,
    changeRewardsInfo,

    removeEducationInfo,
    removeLocalTrainingInfo,
    removeForeignTrainingInfo,
    removeAdditionalProfessionalQualificationInfo,
    removePublicationInfo,
    removeHonourInfo,
    removeRewardInfo,
  };
};
